import { UNASSIGNED_FOLDER_ID } from '@src/stores-v2/FolderStructureStore';
import { resolveDashboardStoryThumbnailUrl } from '@src/utility/story';

import { getStoryLastActivityIso } from './storyActivity';

import type { DashboardStory, FolderStructure } from '@src/types.ts/story';

export interface FolderAggregate {
  /** Total clips across this folder and all descendant folders. */
  clipCount: number;
  /** Total duration of stories in this folder and descendants, in seconds. */
  durationSeconds: number;
  /** Number of stories in this folder and descendants. */
  storyCount: number;
  /**
   * Newest activity across this folder and its descendants — the max of
   * `getStoryLastActivityIso` over their stories, so a folder row and the story
   * rows beneath it report the same thing.
   */
  lastUpdate?: string;
  /**
   * Thumbnail of the most-recently-updated story (with a thumbnail) in this
   * folder or its descendants — used as the folder card's cover image.
   */
  coverThumbnailUrl?: string;
}

const timeOf = (iso?: string): number => new Date(iso || 0).getTime();

export function getStoryDurationSeconds(story: DashboardStory): number {
  const candidates = [
    story.finalVideo?.videoFilePrimary?.video?.duration,
    story.selfHostedVideo?.video?.duration,
    story.originalVideo?.video?.duration,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const n = typeof c === 'string' ? Number(c) : c;
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/**
 * Compute clip / duration / story-count aggregates for every folder in the
 * structure, recursively (descendants roll up into their ancestors).
 *
 * Returns a `Map<folderId, FolderAggregate>`. Folders absent from the map have
 * no stories assigned (count to zero). Also emits an `UNASSIGNED_FOLDER_ID`
 * bucket aggregating stories with no folder, so the virtual "Unassigned" tile
 * reads consistently with real folders.
 */
export function computeFolderAggregates(
  structure: FolderStructure,
  stories: DashboardStory[],
): Map<string, FolderAggregate> {
  const out = new Map<string, FolderAggregate>();
  if (!structure.folders.length) return out;

  // Initialize zero-aggregate for every known folder so callers can read
  // without nullish-checks. The virtual "Unassigned" bucket collects stories
  // with no folder assignment, computed identically to real folders so its
  // tile (duration / last update / cover) reads consistently with them.
  for (const f of structure.folders) {
    out.set(f.id, { clipCount: 0, durationSeconds: 0, storyCount: 0 });
  }
  out.set(UNASSIGNED_FOLDER_ID, {
    clipCount: 0,
    durationSeconds: 0,
    storyCount: 0,
  });

  // Tracks the activity timestamp of the story whose thumbnail currently serves
  // as each folder's cover, so the cover follows the most-recently-active story.
  const coverAt = new Map<string, string>();

  // Aggregate each story into its direct folder — unfoldered stories fall into
  // the `UNASSIGNED_FOLDER_ID` bucket.
  for (const story of stories) {
    const folderId = structure.storyFolderMap[story.id] ?? UNASSIGNED_FOLDER_ID;
    const bucket = out.get(folderId);
    if (!bucket) continue;
    bucket.storyCount += 1;
    bucket.clipCount += story.otherVideos?.length ?? 0;
    bucket.durationSeconds += getStoryDurationSeconds(story);
    const activityAt = getStoryLastActivityIso(story);
    if (activityAt) {
      if (
        !bucket.lastUpdate ||
        timeOf(activityAt) > timeOf(bucket.lastUpdate)
      ) {
        bucket.lastUpdate = activityAt;
      }
    }

    // Folder cover = thumbnail of the most-recent story that has one. Keyed on
    // the same activity value as `lastUpdate`, so the tile's image and its date
    // always point at the same story.
    const thumb = resolveDashboardStoryThumbnailUrl(story);
    if (thumb) {
      const at = activityAt ?? '';
      if (
        !bucket.coverThumbnailUrl ||
        timeOf(at) > timeOf(coverAt.get(folderId))
      ) {
        bucket.coverThumbnailUrl = thumb;
        coverAt.set(folderId, at);
      }
    }
  }

  // Roll up children into ancestors. Process leaves-first by sorting on depth
  // (computed from the parentId chain).
  const depthOf = new Map<string, number>();
  const computeDepth = (folderId: string, guard: Set<string>): number => {
    if (depthOf.has(folderId)) return depthOf.get(folderId) ?? 0;
    if (guard.has(folderId)) return 0;
    guard.add(folderId);
    const folder = structure.folders.find(f => f.id === folderId);
    if (!folder) return 0;
    const d = folder.parentId ? 1 + computeDepth(folder.parentId, guard) : 0;
    depthOf.set(folderId, d);
    return d;
  };
  for (const f of structure.folders) computeDepth(f.id, new Set());

  const byDepthDesc = [...structure.folders].sort(
    (a, b) => (depthOf.get(b.id) ?? 0) - (depthOf.get(a.id) ?? 0),
  );

  for (const f of byDepthDesc) {
    if (!f.parentId) continue;
    const me = out.get(f.id);
    const parent = out.get(f.parentId);
    if (!me || !parent) continue;
    parent.clipCount += me.clipCount;
    parent.durationSeconds += me.durationSeconds;
    parent.storyCount += me.storyCount;
    if (me.lastUpdate) {
      if (
        !parent.lastUpdate ||
        new Date(me.lastUpdate).getTime() >
          new Date(parent.lastUpdate).getTime()
      ) {
        parent.lastUpdate = me.lastUpdate;
      }
    }
    // Inherit a descendant's cover when it's more recent than the parent's own.
    if (me.coverThumbnailUrl) {
      const childAt = coverAt.get(f.id) ?? '';
      if (
        !parent.coverThumbnailUrl ||
        timeOf(childAt) > timeOf(coverAt.get(f.parentId))
      ) {
        parent.coverThumbnailUrl = me.coverThumbnailUrl;
        coverAt.set(f.parentId, childAt);
      }
    }
  }

  return out;
}

/**
 * Format a duration in seconds as a compact label: "1.5 hr", "12 min",
 * "45 sec", or undefined when zero.
 */
export function formatDurationCompact(seconds: number): string | undefined {
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
  if (seconds >= 3600) {
    const hours = seconds / 3600;
    const rounded =
      hours >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10;
    return `${rounded} hr`;
  }
  if (seconds >= 60) {
    return `${Math.round(seconds / 60)} min`;
  }
  return `${Math.round(seconds)} sec`;
}
